import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import axios from "axios";
import TooltippedElement from "components/TooltippedElement";
import { useModalContext } from "components/modal";
import { Button } from "components/button";
import PlanOptionModal from "./components/PlanOptionModal";
import AddPlanModal from "./components/AddPlanModal";
import { RiFileForbidFill, RiFileCloudLine, RiMore2Fill } from "react-icons/ri";
import { HiChevronDoubleDown, HiChevronDoubleUp } from "react-icons/hi";
import { ICustomerPlan, IPlan } from "./interface"
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import Switch from "rc-switch";

interface Props {
  id: string;
  email: string;
  setShowArchivedServicePlans?: (show: boolean) => void;
};

export default function ServicePlans(props: Props) {
  const { t } = useTranslation();
  const tBase = "views.service-plans.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { setModal } = useModalContext();
  const customerEmail = props.email;
  const customerId = props.id;
  const [showDescription, setShowDescription] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);

  const toggleDescription = (planId: number) => {
    setShowDescription((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(planId)) {
        newSet.delete(planId);
      } else {
        newSet.add(planId);
      }
      return newSet;
    });
  };

  const plansData = useQuery(
    "plans",
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/customers?type=PLANS`
        )
        .then((res) => {
          // return array of plans sorted alphabetically by name
          return res.data?.sort(
            (
              a: { name: string; }, 
              b: { name: string; }
            ) => {
            const nameA = a.name?.toLowerCase() || "";
            const nameB = b.name?.toLowerCase() || "";
            return nameA.localeCompare(nameB);
          })
        })
  );

  const customerPlans = useQuery(
    ["customerPlans", customerId, "plans"],
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/customers?type=CUSPLANS`,
          { params: { id: customerId } }
        )
        .then(res => res.data)
  );

  const getPDFcontract = useMutation(async (info: { servicePlanId: string; customerId: string }) => {
    return axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/file`,
        { params: info }
      )
      .then((res) => {
        return res.data;
      });
  });

  const columns = [
    { header: tr("Plan Name"), width: "w-[15%]" },
    { header: tr("Plan Description"), width: "w-[35%]"},
    { header: "", width: "w-[4%]" },
    { header: tr("Property"), width: "w-[17%]"},
    { header: tr("Approval Date"), width: "w-[9%] text-center"},
    { header: tr("Start Date"), width: "w-[9%] text-center"},
    { header: tr("Duration"), subHeader: tr("(Months)"), width: "w-[6%] text-center"},
    { header: tr("Options"), width: "w-[5%] text-center"},
  ];

	return (
    <div className="mb-10">
      <div className="flex justify-between items-center mt-2 mb-4">
        <h1>{tr("Service Plans")}:</h1>
        <div className="flex items-center gap-4">
          <div className="flex-wrap flex items-center gap-2">
            <div className="text-sm text-gray-700">{tr("Show Archived")}</div>
            <Switch 
              checked={showArchived}
              onChange={() => {
                setShowArchived(!showArchived);
                props.setShowArchivedServicePlans && props.setShowArchivedServicePlans(!showArchived);
              }}
            />
          </div>
          <Button
            primary
            onClick={() => {
              setModal({
                label: tr("Add Plan"),
                component: (
                  <AddPlanModal
                    customerId={customerId}
                    customerEmail={customerEmail}
                    customerPlans={customerPlans.data}
                  />
                ),
              });
            }}
          >
            {tr("Add Plan")}
          </Button>
        </div>
      </div>

      <div>
        <div className="flex border-b-2 border-gray-300">
          {columns.map((col) => (
            <div key={col.header} className={"p1 " + col.width}>
              {col.header}
              {col.subHeader && (<div className="text-xs text-gray-500">{col.subHeader}</div>)}
            </div>
          ))}
        </div>
        <div>
          {customerPlans.data
            ?.map((customer: ICustomerPlan) => {
              const plan = plansData.data?.find(
                (x: { id: { toString: () => string } }) =>
                  x.id?.toString() === customer.service_planid?.toString()
              );

              return { customer, plan };
            })
            .filter(
              ({ customer, plan }: { customer: ICustomerPlan; plan: IPlan }) => {
                // filter out archived plans if showArchived is false
                return (
                  (showArchived || !customer.archived) &&
                  (plan?.archived === false || !plan?.archived)
                );
              }
            )
            .sort(
              (
                a: { plan: { name: string } },
                b: { plan: { name: string } }
              ) => {
                // sorts alphabetically, by plan name
                const nameA = a.plan?.name?.toLowerCase() || "";
                const nameB = b.plan?.name?.toLowerCase() || "";
                return nameA.localeCompare(nameB);
              }
            )
            .map(({ customer, plan }: { customer: ICustomerPlan; plan: IPlan }, i: number) => {
              const isContract = plan?.is_contract;

              return (
                <div
                  key={customer.id}
                  className={`
                    flex border-b border-gray-300 pt-3 pb-2 pl-2 text-gray-800
                    ${customer.archived ? "bg-red-100" : "bg-white"}
                  `}
                >
                  {/* Plan Name */}
                  <div className={columns[0].width}>
                    <div className="flex items-center gap-2">
                      <TooltippedElement
                        message={
                          isContract
                            ? tr("View approved contract")
                            : tr("Contract not required")
                        }
                        placement="bottom"
                        className="-mb-1"
                        element={
                          <button
                            className={`${
                              isContract ? "cursor-pointer" : "cursor-auto"
                            }`}
                            onClick={async () => {
                              if (!isContract) return;
                              getPDFcontract
                                .mutateAsync({
                                  servicePlanId: customer.service_planid,
                                  customerId: customer.business_customerid,
                                })
                                .then((res) => window.open(res));
                            }}
                          >
                            {isContract ? (
                              <RiFileCloudLine className="text-green-500" />
                            ) : (
                              <RiFileForbidFill className="text-red-500" />
                            )}
                          </button>
                        }
                      />
                      <p>{plan?.name}</p>
                    </div>
                  </div>
                  {/* Plan Description */}
                  <div className={columns[1].width}>
                    <div
                      className={`
                        transition-all duration-300 ease-in-out overflow-hidden
                        ${
                          showDescription.has(i)
                            ? "max-h-[300px] whitespace-pre-wrap text-gray-700 overflow-y-auto"
                            : "max-h-[22.5px] truncate"
                        }
                      `}
                    >
                      {plan?.description}
                    </div>
                  </div>
                  {/* Toggle for description */}
                  <div className={columns[2].width}>
                    <button onClick={() => toggleDescription(i)}>
                      {showDescription.has(i) ? (
                        <HiChevronDoubleUp size={20} className="text-primary hover:bg-slate-100 hover:border rounded-[5px]" />
                      ) : (
                        <HiChevronDoubleDown
                          size={20}
                          className="text-primary hover:bg-slate-100 hover:border rounded-[5px]"
                        />
                      )}
                    </button>
                  </div>
                  {/* Property */}
                  <div className={columns[3].width}>
                    {customer.customer_address?.address +
                      " " +
                      customer.customer_address?.city +
                      ", " +
                      customer.customer_address?.state +
                      " " +
                      customer.customer_address?.zipcode 
                      + (customer.customer_address?.zip_ext ? `-${customer.customer_address?.zip_ext}` : "")
                      || tr("No Property")}
                  </div>
                  {/* Approval Date */}
                  <div className={columns[4].width}>
                    {customer.date_signed ? (
                      <span>{format(new Date(customer.date_signed), "MMM dd, yyyy")}</span>
                    ) : (
                      <span className="text-[#ff500890]">{tr("Pending")}</span>
                    )}
                  </div>
                  {/* Start Date */}
                  <div className={columns[5].width}>
                    {customer.start_date ? format(new Date(customer.start_date), "MMM dd, yyyy") : "--"}
                  </div>
                  {/* Duration */}
                  <div className={columns[6].width}>{plan?.duration}</div>
                  {/* Options */}
                  <div className={columns[7].width}>
                    <button
                      className="hover:bg-slate-100 hover:border rounded-[5px] p-0.5"
                      onClick={() => {
                        setModal({
                          label: tr("Plan Options"),
                          component: (
                            <PlanOptionModal
                              plan={plan}
                              customer={customer}
                              customerId={customerId}
                              customerEmail={customerEmail}
                            />
                          ),
                        });
                      }}
                    >
                      <RiMore2Fill size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
